(function () {
  'use strict';

  const root = document.getElementById('routine-builder');
  if (!root) return;

  const dataEl = document.getElementById('rb-product-data');
  let allProducts = [];
  try {
    allProducts = JSON.parse(dataEl.textContent || '[]');
  } catch (e) {
    console.error('Routine Builder: could not parse product data', e);
  }

  const tagMapEl = document.getElementById('rb-tag-map');
  const tagMap = new Map();
  try {
    const entries = JSON.parse((tagMapEl && tagMapEl.textContent) || '[]');
    entries.forEach((entry) => {
      if (entry.tag) tagMap.set(entry.tag, { label: entry.label, tooltip: entry.tooltip });
    });
  } catch (e) {
    console.error('Routine Builder: could not parse tag map', e);
  }

  // ---- config from section settings ----
  const stepOrder = {
    Skin: splitTags(root.dataset.skinOrder),
    Hair: splitTags(root.dataset.hairOrder),
    Body: splitTags(root.dataset.bodyOrder),
  };
  const addonTags = {
    Skin: splitTags(root.dataset.skinAddons),
    Hair: splitTags(root.dataset.hairAddons),
    Body: splitTags(root.dataset.bodyAddons),
  };
  const concernTags = {
    Skin: splitTags(root.dataset.skinConcerns),
    Hair: splitTags(root.dataset.hairConcerns),
    Body: splitTags(root.dataset.bodyConcerns),
  };

  function splitTags(str) {
    return (str || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }

  // ---- state ----
  const state = {
    category: null, // 'Skin' | 'Hair' | 'Body'
    path: null, // 'full' | 'specific'
    selectedTypes: new Set(), // for specific path — one or more simpletype_ tags
    concerns: new Set(),
    considerations: new Set(),
  };

  // ---- helpers ----
  function labelFromTag(tag) {
    const override = tagMap.get(tag);
    if (override && override.label) return override.label;
    const parts = tag.split('_');
    const last = parts[parts.length - 1];
    return last
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
      .replace(/\bAnd\b/g, '&');
  }

  function tooltipFromTag(tag) {
    const override = tagMap.get(tag);
    return (override && override.tooltip) || '';
  }

  function productsInCategory(category) {
    // A product belongs to a category if its Product Type matches, OR it carries a
    // simpletype_ tag that's registered in that category's step order/add-ons — this lets
    // a multi-use product (e.g. an oil typed as "Body") also surface under Hair or Skin
    // without needing a second Product Type, which Shopify doesn't allow anyway.
    const knownTags = (stepOrder[category] || []).concat(addonTags[category] || []);
    return allProducts.filter(
      (p) => p.type === category || (p.tags || []).some((t) => knownTags.includes(t))
    );
  }

  // Orders AND restricts simpletype_ tags for display: only tags registered in this
  // category's step order/add-ons are shown, in that configured order. This keeps a
  // cross-listed multi-use product (e.g. an oil typed as Body but also tagged for Hair)
  // from leaking its other categories' type tags (like Body Oils) into this category's list.
  function orderTagsForDisplay(tags, category) {
    const order = stepOrder[category] || [];
    const addons = addonTags[category] || [];
    const known = order.concat(addons);
    return known.filter((t) => tags.includes(t));
  }

  // Pool used for discovering which concern/consideration options to show —
  // scoped to the selected types when on the specific path, full category otherwise.
  function currentPool() {
    const products = productsInCategory(state.category);
    if (state.path === 'specific' && state.selectedTypes.size) {
      return products.filter((p) => Array.from(state.selectedTypes).some((t) => (p.tags || []).includes(t)));
    }
    return products;
  }

  function orderedSelectedTypes() {
    const order = stepOrder[state.category] || [];
    const ordered = order.filter((t) => state.selectedTypes.has(t));
    const remaining = Array.from(state.selectedTypes)
      .filter((t) => !order.includes(t))
      .sort();
    return ordered.concat(remaining);
  }

  function tagsWithPrefix(products, prefix) {
    const found = new Set();
    products.forEach((p) => (p.tags || []).forEach((t) => {
      if (t.indexOf(prefix) === 0) found.add(t);
    }));
    return Array.from(found).sort();
  }

  function goToStep(n) {
    root.querySelectorAll('.rb-step').forEach((el) => {
      el.dataset.active = String(el.dataset.step) === String(n);
    });
    const fill = root.querySelector('.rb-progress-fill');
    if (fill) fill.style.width = (n / 5) * 100 + '%';
  }

  // ---- pill rendering (multi-select toggle, used for concerns & considerations) ----
  function renderPills(container, tags, selectedSet) {
    container.innerHTML = '';
    tags.forEach((tag) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rb-pill';
      btn.textContent = labelFromTag(tag);
      btn.dataset.tag = tag;
      const tooltip = tooltipFromTag(tag);
      if (tooltip) {
        btn.setAttribute('data-tooltip', tooltip);
        btn.setAttribute('aria-label', `${labelFromTag(tag)}: ${tooltip}`);
      }
      if (selectedSet.has(tag)) btn.classList.add('is-selected');
      btn.addEventListener('click', () => {
        if (selectedSet.has(tag)) {
          selectedSet.delete(tag);
          btn.classList.remove('is-selected');
        } else {
          selectedSet.add(tag);
          btn.classList.add('is-selected');
        }
      });
      container.appendChild(btn);
    });
  }

  // ---- step builders ----
  function buildStep2() {
    const products = productsInCategory(state.category);
    const container = root.querySelector('[data-role="type-pills"]');
    container.innerHTML = '';
    state.selectedTypes.clear();
    updateStep2NextState();

    const tags = orderTagsForDisplay(tagsWithPrefix(products, 'simpletype_'), state.category);
    tags.forEach((tag) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rb-pill';
      btn.textContent = labelFromTag(tag);
      btn.dataset.tag = tag;
      btn.addEventListener('click', () => {
        if (state.selectedTypes.has(tag)) {
          state.selectedTypes.delete(tag);
          btn.classList.remove('is-selected');
        } else {
          state.selectedTypes.add(tag);
          btn.classList.add('is-selected');
        }
        updateStep2NextState();
      });
      container.appendChild(btn);
    });
  }

  function updateStep2NextState() {
    const nextBtn = root.querySelector('[data-role="step2-next"]');
    if (nextBtn) nextBtn.disabled = state.selectedTypes.size === 0;
  }

  function buildStep3() {
    const container = root.querySelector('[data-role="concern-pills"]');
    const allowed = concernTags[state.category] || [];
    const tags = tagsWithPrefix(currentPool(), 'concern_').filter((t) => allowed.includes(t));
    state.concerns.clear();
    renderPills(container, tags, state.concerns);
  }

  function buildConsiderationStep() {
    const container = root.querySelector('[data-role="consider-pills"]');
    const tags = tagsWithPrefix(currentPool(), 'consider_');
    state.considerations.clear();
    renderPills(container, tags, state.considerations);
  }

  // ---- filtering & results ----
  function matchesConsiderations(product) {
    // Considerations are a hard filter — a product must have every selected tag to appear at all.
    if (state.considerations.size === 0) return true;
    return Array.from(state.considerations).every((tag) => (product.tags || []).includes(tag));
  }

  function concernScore(product) {
    // Concerns are a relevance signal, not a filter — a product with none of the selected
    // concern tags can still appear, it just ranks lower than products that match more of them.
    if (state.concerns.size === 0) return 0;
    return Array.from(state.concerns).reduce(
      (score, tag) => score + ((product.tags || []).includes(tag) ? 1 : 0),
      0
    );
  }

  function sortByRelevance(products) {
    return products.slice().sort((a, b) => {
      const scoreDiff = concernScore(b) - concernScore(a);
      if (scoreDiff !== 0) return scoreDiff;
      // equal relevance — fall back to the source collection's manual sort order,
      // so merchandising priority still applies when concerns don't differentiate.
      return (a.position || 0) - (b.position || 0);
    });
  }

  function buildResults() {
    const heading = root.querySelector('[data-role="results-heading"]');
    const body = root.querySelector('[data-role="results-body"]');
    body.innerHTML = '';

    const pool = productsInCategory(state.category).filter(matchesConsiderations);
    const isFull = state.path === 'full';
    heading.textContent = isFull ? `Your ${state.category} Routine` : `Your ${state.category} Selection`;

    const order = isFull ? (stepOrder[state.category] || []) : orderedSelectedTypes();
    const picked = [];
    const usedIds = new Set();

    order.forEach((stepTag) => {
      const stepMatches = sortByRelevance(
        pool.filter((p) => (p.tags || []).includes(stepTag) && !usedIds.has(p.id))
      );
      if (stepMatches.length === 0) return;
      const group = document.createElement('div');
      group.className = 'rb-result-group';
      const groupTitle = document.createElement('p');
      groupTitle.className = 'rb-result-group-title';
      groupTitle.textContent = labelFromTag(stepTag);
      group.appendChild(groupTitle);
      const grid = document.createElement('div');
      grid.className = 'rb-product-grid';
      group.appendChild(grid);
      renderProductGrid(grid, stepMatches.slice(0, 4));
      body.appendChild(group);
      stepMatches.slice(0, 4).forEach((p) => {
        usedIds.add(p.id);
        picked.push(p);
      });
    });

    // "Also recommended" add-ons only make sense for the curated Full Routine —
    // if the person hand-picked specific types, they've already told us what they want.
    if (isFull) {
      const addonTagsForCategory = addonTags[state.category] || [];
      if (addonTagsForCategory.length) {
        const addonMatches = sortByRelevance(
          pool.filter(
            (p) => addonTagsForCategory.some((t) => (p.tags || []).includes(t)) && !usedIds.has(p.id)
          )
        );
        if (addonMatches.length) {
          const group = document.createElement('div');
          group.className = 'rb-result-group';
          const groupTitle = document.createElement('p');
          groupTitle.className = 'rb-result-group-title';
          groupTitle.textContent = 'Also recommended';
          group.appendChild(groupTitle);
          const grid = document.createElement('div');
          grid.className = 'rb-product-grid';
          group.appendChild(grid);
          renderProductGrid(grid, addonMatches.slice(0, 4));
          body.appendChild(group);
          addonMatches.slice(0, 4).forEach((p) => picked.push(p));
        }
      }
    }

    if (picked.length === 0 && body.children.length === 0) {
      body.innerHTML = '<p class="rb-empty">No products match that combination yet — try removing a consideration.</p>';
    }
  }

  function renderProductGrid(container, products) {
    products.forEach((p, index) => {
      const card = document.createElement('div');
      card.className = 'rb-product-card';
      card.dataset.variantId = p.variantId || '';
      // top-ranked product per group is pre-selected so Add All works with zero extra taps;
      // the person can tap another tick to swap the pick within that group.
      if (index === 0 && p.variantId) card.classList.add('is-selected');

      const imageWrap = document.createElement('div');
      imageWrap.className = 'rb-product-image';
      imageWrap.setAttribute('role', 'button');
      imageWrap.setAttribute('tabindex', '0');
      imageWrap.setAttribute('aria-label', `Select ${p.title}`);
      if (p.image) {
        const img = document.createElement('img');
        img.src = p.image;
        img.alt = p.title;
        img.loading = 'lazy';
        imageWrap.appendChild(img);
      }

      const toggleSelection = () => {
        const alreadySelected = card.classList.contains('is-selected');
        container.querySelectorAll('.rb-product-card').forEach((c) => c.classList.remove('is-selected'));
        if (!alreadySelected) card.classList.add('is-selected');
      };
      imageWrap.addEventListener('click', toggleSelection);
      imageWrap.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleSelection();
        }
      });

      const tick = document.createElement('span');
      tick.className = 'rb-select-tick';
      tick.setAttribute('aria-hidden', 'true');
      tick.innerHTML =
        '<svg viewBox="0 0 16 16" width="14" height="14"><path d="M3 8.5L6.5 12L13 4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      imageWrap.appendChild(tick);
      card.appendChild(imageWrap);

      const titleLink = document.createElement('a');
      titleLink.className = 'rb-product-title';
      titleLink.href = p.url;
      if (p.vendor) {
        const brand = document.createElement('strong');
        brand.className = 'rb-product-brand';
        brand.textContent = p.vendor;
        titleLink.appendChild(brand);
        titleLink.appendChild(document.createTextNode(' ' + p.title));
      } else {
        titleLink.textContent = p.title;
      }
      titleLink.addEventListener('click', (e) => {
        e.preventDefault();
        openQuickView(p);
      });
      card.appendChild(titleLink);

      const price = document.createElement('span');
      price.className = 'rb-product-price';
      price.textContent = p.price;
      card.appendChild(price);

      container.appendChild(card);
    });
  }

  // ---- quick view ----
  const qvOverlay = root.querySelector('[data-role="quickview-overlay"]');
  const qvImage = root.querySelector('[data-role="quickview-image"]');
  const qvTitle = root.querySelector('[data-role="quickview-title"]');
  const qvPrice = root.querySelector('[data-role="quickview-price"]');
  const qvDescription = root.querySelector('[data-role="quickview-description"]');
  const qvViewFull = root.querySelector('[data-role="quickview-view-full"]');

  function truncate(str, max) {
    if (!str) return '';
    return str.length > max ? str.slice(0, max).trimEnd() + '…' : str;
  }

  function openQuickView(product) {
    if (!qvOverlay) return;
    qvImage.innerHTML = product.image
      ? `<img src="${product.image}" alt="${product.title.replace(/"/g, '&quot;')}">`
      : '';

    qvTitle.innerHTML = '';
    if (product.vendor) {
      const brand = document.createElement('strong');
      brand.className = 'rb-product-brand';
      brand.textContent = product.vendor;
      qvTitle.appendChild(brand);
      qvTitle.appendChild(document.createTextNode(' ' + product.title));
    } else {
      qvTitle.textContent = product.title;
    }

    qvPrice.textContent = product.price || '';
    qvDescription.textContent = truncate(product.description, 250);
    qvViewFull.href = product.url;

    qvOverlay.hidden = false;
    document.documentElement.classList.add('rb-modal-open');
  }

  function closeQuickView() {
    if (!qvOverlay) return;
    qvOverlay.hidden = true;
    document.documentElement.classList.remove('rb-modal-open');
  }

  if (qvOverlay) {
    qvOverlay.addEventListener('click', (e) => {
      if (e.target === qvOverlay) closeQuickView();
    });
    root.querySelector('[data-role="quickview-close"]').addEventListener('click', closeQuickView);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !qvOverlay.hidden) closeQuickView();
    });
  }

  // ---- add all to cart ----
  async function addAllToCart() {
    const btn = root.querySelector('[data-role="add-all"]');
    const selectedCards = Array.from(
      root.querySelectorAll('[data-role="results-body"] .rb-product-card.is-selected')
    );
    const ids = selectedCards
      .map((c) => c.dataset.variantId)
      .filter(Boolean)
      .map((id) => Number(id));
    if (!ids.length) return;

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Adding…';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })) }),
      });
      if (!res.ok) throw new Error('Add to cart failed');
      btn.textContent = 'Refresh page to update cart';
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      // stays disabled/greyed out — no revert, since the cart drawer needs a refresh to reflect the add
    } catch (err) {
      console.error(err);
      btn.textContent = 'Something went wrong';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 2000);
    }
  }

  // ---- wire up navigation ----
  root.querySelectorAll('[data-category]').forEach((btn) => {
    btn.addEventListener('click', () => {
      state.category = btn.dataset.category;
      buildStep2();
      goToStep(2);
    });
  });

  root.querySelector('[data-role="full-routine"]').addEventListener('click', () => {
    state.path = 'full';
    state.selectedTypes.clear();
    buildStep3();
    goToStep(3);
  });

  root.querySelector('[data-role="step2-next"]').addEventListener('click', () => {
    if (state.selectedTypes.size === 0) return;
    state.path = 'specific';
    buildStep3();
    goToStep(3);
  });

  root.querySelectorAll('[data-back]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const current = Number(btn.closest('.rb-step').dataset.step);
      if (current === 2) goToStep(1);
      else if (current === 3) goToStep(2);
      else if (current === 4) goToStep(3);
    });
  });

  root.querySelector('[data-step="3"] [data-next]').addEventListener('click', () => {
    buildConsiderationStep();
    goToStep(4);
  });

  root.querySelector('[data-role="show-results"]').addEventListener('click', () => {
    buildResults();
    goToStep(5);
  });

  root.querySelector('[data-role="add-all"]').addEventListener('click', addAllToCart);

  root.querySelector('[data-role="start-over"]').addEventListener('click', () => {
    state.category = null;
    state.path = null;
    state.selectedTypes.clear();
    state.concerns.clear();
    state.considerations.clear();
    goToStep(1);
  });
})();
